import { msg } from "@lingui/core/macro";
import { createEffect, createSignal, For } from "solid-js";
import { createStore, reconcile } from "solid-js/store";
import { TransitionGroup } from "solid-transition-group";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { createPowerSyncQuery } from "@listto/shared/solid";
import { keepPreviousData } from "@tanstack/solid-query";
import {
  cn,
  createSubtaskMutation,
  deleteSubtaskMutation,
  getSubtasksByTodoQuery,
  queryKeys,
  toggleSubtaskMutation,
  updateSubtaskMutation,
} from "@listto/shared";
import type { SubtaskRecord } from "@listto/powersync";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Plus, Trash2 } from "lucide-solid";
import { useSession } from "@/lib/auth";
import { DEBOUNCE_DELAY_MS, OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";
import { useI18n } from "@/providers/i18n-provider";
import { useTodoFocus } from "../hooks/use-todo-focus";

interface SubtaskListProps {
  todoId: string;
}

export function SubtaskList(props: SubtaskListProps) {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();
  const [newSubtaskText, setNewSubtaskText] = createSignal("");
  const [isSubmitting, setIsSubmitting] = createSignal(false);
  const [stepInput, setStepInput] = createSignal<HTMLInputElement | null>(null);
  const { lastFocusedTodoInput, setLastFocusedTodoInput } = useTodoFocus();

  const { userId } = session;

  createEffect(() => {
    // Track props.todoId
    void props.todoId;
    requestAnimationFrame(() => {
      const input = stepInput();
      if (lastFocusedTodoInput() === "subtask" && input) {
        input.focus();
        const len = input.value.length;
        input.setSelectionRange(len, len);
      }
    });
  });

  const subtasks = createPowerSyncQuery<SubtaskRecord>({
    queryKey: () => queryKeys.subtasks.byTodo(props.todoId),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance ? getSubtasksByTodoQuery(dbInstance, props.todoId) : null;
    },
    enabled: () => Boolean(props.todoId) && Boolean(drizzle()),
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
    placeholderData: keepPreviousData,
    suspense: false,
  });

  const [subtasksStore, setSubtasksStore] = createStore<SubtaskRecord[]>([]);

  createEffect(() => {
    if (subtasks.data) {
      setSubtasksStore(reconcile(subtasks.data, { key: "id" }));
    }
  });

  const handleAddSubtask = async (e: Event) => {
    e.preventDefault();
    const text = newSubtaskText().trim();
    const dbInstance = drizzle();
    const uid = userId();
    if (!text || !dbInstance || !uid) {
      return;
    }

    setIsSubmitting(true);
    try {
      await createSubtaskMutation(dbInstance, props.todoId, text, subtasks.data ?? []);
      setNewSubtaskText("");
    } catch (err) {
      console.error("Error creating subtask:", err);
    } finally {
      setIsSubmitting(false);
      setTimeout(() => {
        stepInput()?.focus();
      }, 0);
    }
  };

  const handleToggleSubtask = async (subtask: SubtaskRecord) => {
    const dbInstance = drizzle();
    if (!dbInstance) {
      return;
    }
    try {
      await toggleSubtaskMutation(dbInstance, subtask.id, subtask.completed === 1);
    } catch (err) {
      console.error("Error toggling subtask:", err);
    }
  };

  const handleUpdateSubtaskText = async (id: string, text: string) => {
    const dbInstance = drizzle();
    if (!dbInstance) {
      return;
    }
    try {
      await updateSubtaskMutation(dbInstance, id, { text });
    } catch (err) {
      console.error("Error updating subtask:", err);
    }
  };

  const subtaskTimeouts: Record<string, ReturnType<typeof setTimeout>> = {};
  const debouncedUpdateSubtaskText = (id: string, text: string) => {
    if (subtaskTimeouts[id]) {
      clearTimeout(subtaskTimeouts[id]);
    }
    subtaskTimeouts[id] = setTimeout(() => {
      void handleUpdateSubtaskText(id, text);
    }, DEBOUNCE_DELAY_MS);
  };

  const handleDeleteSubtask = async (id: string) => {
    const dbInstance = drizzle();
    if (!dbInstance) {
      return;
    }
    try {
      await deleteSubtaskMutation(dbInstance, id);
    } catch (err) {
      console.error("Error deleting subtask:", err);
    }
  };

  return (
    <div class="flex flex-col relative">
      <TransitionGroup name="step-item">
        <For each={subtasksStore}>
          {(subtask) => (
            <div class="group flex items-start gap-2 mb-3">
              <Checkbox
                checked={subtask.completed === 1}
                onChange={() => void handleToggleSubtask(subtask)}
                class="scale-90 mt-0.5 shrink-0"
              />
              <textarea
                value={subtask.text ?? ""}
                rows={1}
                onInput={(e) => {
                  e.currentTarget.style.height = "auto";
                  e.currentTarget.style.height = `${e.currentTarget.scrollHeight}px`;
                  debouncedUpdateSubtaskText(subtask.id, e.currentTarget.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    e.currentTarget.blur();
                  }
                }}
                ref={(el) => {
                  requestAnimationFrame(() => {
                    const sh = el.scrollHeight;
                    if (sh > 0) {
                      el.style.height = "auto";
                      el.style.height = `${sh}px`;
                    } else {
                      el.style.height = "auto";
                    }
                  });
                }}
                class={cn(
                  "flex-1 border-none outline-none focus:outline-none bg-transparent p-0 text-sm focus-visible:ring-0 transition-all duration-200 resize-none overflow-hidden [field-sizing:content]",
                  subtask.completed === 1 && "text-muted-foreground line-through opacity-60",
                )}
              />
              <Button
                variant="ghost"
                size="icon"
                class="h-6 w-6 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus:opacity-100 transition-opacity shrink-0"
                onClick={() => void handleDeleteSubtask(subtask.id)}
              >
                <Trash2 class="h-3 w-3 text-muted-foreground" />
              </Button>
            </div>
          )}
        </For>
      </TransitionGroup>

      <form
        onSubmit={(e) => void handleAddSubtask(e)}
        class="flex items-center gap-2 group/stepinput relative"
      >
        <div class="flex justify-center w-5 shrink-0">
          <Plus class="h-4.5 w-4.5 text-muted-foreground" />
        </div>
        <input
          ref={setStepInput}
          type="text"
          placeholder={t(msg`Add step`)}
          value={newSubtaskText()}
          onInput={(e) => void setNewSubtaskText(e.currentTarget.value)}
          onFocus={() => setLastFocusedTodoInput("subtask")}
          onBlur={() => {
            if (lastFocusedTodoInput() === "subtask") {
              setLastFocusedTodoInput(null);
            }
          }}
          class="todo-subtask-add-input flex-1 border-none outline-none bg-transparent p-0 text-sm focus-visible:ring-0 placeholder:text-muted-foreground"
          disabled={isSubmitting()}
        />
        <kbd
          class="mr-1 h-4.5 min-w-[15px] inline-flex items-center justify-center rounded border border-primary/20 bg-primary/5 px-1.5 font-mono text-[9px] font-bold text-primary opacity-0 group-hover/stepinput:opacity-100 group-focus-within/stepinput:opacity-100 transition-opacity select-none cursor-help"
          title={t(msg`Add step: Press Y to add a new step`)}
        >
          {t(msg`Y`)}
        </kbd>
      </form>
    </div>
  );
}
